import { useCallback, useRef } from "react";
import { pushPageViewEvent } from "@mlb/analytics";
import { RouteConstants } from "../constants/RouteConstants";

const validPathForAnalytics = (path, previousPath) => {
  if (path === previousPath) {
    return false;
  }
  const isLoginPage = path === RouteConstants.LOGIN || path === RouteConstants.OKTA_LOGIN;
  const isExamplesPage = path === RouteConstants.EXAMPLES;
  return !isLoginPage && !isExamplesPage;
};

const routeToRegex = route => {
  const escaped = route
    .replace(/\(\\d\+\)/g, "")
    .replace(/:[^/]+/g, "[^/]+")
    .replace(/\//g, "\\/");
  return new RegExp(`^${escaped}$`);
};

const ROUTE_SECTIONS = [
  { route: RouteConstants.OKTA_LOGIN, section: "Okta Login" },
  { route: RouteConstants.LOGIN, section: "Login" },
  { route: RouteConstants.EXAMPLES, section: "Examples" },
  { route: RouteConstants.MY_ACCOUNT, section: "My Account" },

  { route: RouteConstants.USER_EDIT, section: "User Edit" },
  { route: RouteConstants.ADMIN_USERS, section: "Admin Users" },
  { route: RouteConstants.DEPARTMENT_EDIT, section: "Department Edit" },
  { route: RouteConstants.ADMIN_DEPARTMENTS, section: "Admin Departments" },
  { route: RouteConstants.VENUE_PRICES_BULK_OVERRIDE, section: "Venue Prices Bulk Override" },
  { route: RouteConstants.VENUE_EDIT, section: "Venue Edit" },
  { route: RouteConstants.ADMIN_VENUES, section: "Admin Venues" },
  { route: RouteConstants.ADMIN_SUPPORT, section: "Admin Support" },
  { route: RouteConstants.ADMIN_EVENTS, section: "Admin Events" },

  { route: RouteConstants.TICKET_EVENTS, section: "Ticket Events" },
  { route: RouteConstants.TRANSFER_TICKET_REQUEST, section: "Transfer Request" },
  { route: RouteConstants.MULTI_REQUEST, section: "Multi Request" },
  { route: RouteConstants.EVENT_REQUESTS, section: "Event Requests" },
  { route: RouteConstants.PARKING_REQUESTS, section: "Parking Requests" },
  { route: RouteConstants.EVENT_RESERVATIONS, section: "Event Reservations" },
  { route: RouteConstants.EVENT_DETAILS, section: "Event Details" },

  { route: RouteConstants.RESERVATIONS_EDIT, section: "Reservations Edit" },
  { route: RouteConstants.RESERVATIONS, section: "Reservations" },

  { route: RouteConstants.USER_SUMMARY, section: "User Summary" },
  { route: RouteConstants.REPORT_EVENTS, section: "Report Events" },
  { route: RouteConstants.REPORT_USERS, section: "Report Users" },
  { route: RouteConstants.REPORT_HISTORY, section: "Report History" },
  { route: RouteConstants.EXPORT_SETTINGS, section: "Export Settings" },

  { route: RouteConstants.SYSTEM_SEASONS_EDIT, section: "System Seasons Edit" },
  { route: RouteConstants.SYSTEM_SEASONS, section: "System Seasons" },

  { route: RouteConstants.ORG_SETTING, section: "Org Setting" },
  { route: RouteConstants.CONTACTS, section: "Contacts" },
  { route: RouteConstants.INVESTIGATIONS, section: "Investigations" },

  { route: RouteConstants.BASE, section: "Home" }
].map(({ route, section }) => ({
  pattern: routeToRegex(route),
  section
}));

const getSiteSection = path => {
  const match = ROUTE_SECTIONS.find(({ pattern }) => pattern.test(path));
  return match ? match.section : path;
};

export const useTracking = userInfo => {
  const previousPathnameRef = useRef();

  const pushPageView = useCallback(
    pathname => {
      if (!!userInfo?.userId) {
        // Only fire analytics event if the pathname has changed, not URL params
        const siteSection = getSiteSection(pathname);
        if (validPathForAnalytics(pathname, previousPathnameRef.current)) {
          const rolePermissions = userInfo.authorities?.reduce(
            (acc, role) => ({ ...acc, [role.name]: String(role.id) }),
            {}
          );
          pushPageViewEvent?.({
            user: {
              okta_id: String(userInfo.oktaId),
              role_permissions: Array(rolePermissions),
              // default to 1 if no org stats id is found for MLB user
              org_id: String(userInfo.org?.statsId ?? 1),
              comptix_id: String(userInfo.userId || -1)
            },
            page: {
              url_path: pathname,
              site_section: siteSection,
              title: siteSection
            },
            baseball: {
              playerId: String(userInfo?.statsId)
            }
          });
          previousPathnameRef.current = pathname;
        }
      }
    },
    [userInfo, previousPathnameRef]
  );

  return { pushPageView };
};
